// ConfigContext.jsx
import React from "react";

/* 
 * NOTE: All styling is produced by compiled main.scss at build time.
 * This provider does NOT inject any CSS variables or styles at runtime.
 */

const defaultConfig = {
    siteName: process.env.REACT_APP_SITE_NAME || "",
    homeTitle: "",
    tagline: "",
    icons: {
        favicon: "/favicon.ico",
        appleTouchIcon: "/logo192.png",
        manifest: "/manifest.json"
    }
};

export const ConfigContext = React.createContext(defaultConfig);

export function useConfig() {
    return React.useContext(ConfigContext);
}

// --- tiny DOM helpers (metadata only) ---------------------------------------
function upsertMeta(name, content) {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href) {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}
// ---------------------------------------------------------------------------

export function ConfigProvider({ children }) {
    const [cfg, setCfg] = React.useState(null);
    const [error, setError] = React.useState(null);

    // Load config from same origin (served by ASP.NET Core in wwwroot)
    React.useEffect(() => {
        let cancelled = false;
        fetch("site-config.json", { cache: "no-store" })
            .then(r => (r.ok ? r.json() : Promise.reject(r.statusText)))
            .then(json => {
                if (!cancelled) {
                    setCfg({
                        ...defaultConfig,
                        ...json,
                        icons: { ...defaultConfig.icons, ...(json.icons || {}) }
                    });
                }
            })
            .catch(e => !cancelled && setError(String(e)));
        return () => {
            cancelled = true;
        };
    }, []);

    // Apply identity-only concerns: title, meta description, icons/manifest
    React.useEffect(() => {
        if (!cfg) return;

        const baseTitle = cfg.siteName || defaultConfig.siteName;
        const suffix = cfg.homeTitle || defaultConfig.homeTitle;
        document.title = suffix ? `${baseTitle} � ${suffix}` : baseTitle;

        const metaDescription =
            (cfg.tagline && cfg.tagline.trim()) ||
            `${baseTitle} � ${suffix || "Platform"}`;
        upsertMeta("description", metaDescription);

        upsertLink("icon", cfg.icons?.favicon);
        upsertLink("apple-touch-icon", cfg.icons?.appleTouchIcon);
        upsertLink("manifest", cfg.icons?.manifest);
    }, [cfg]);

    if (error) {
        return (
            <div style={{ padding: 24, color: "crimson", fontFamily: "system-ui, sans-serif" }}>
                Config load error: {error}
            </div>
        );
    }

    if (!cfg) {
        return <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>Loading�</div>;
    }

    return (
        <ConfigContext.Provider value={cfg}>
            {children}
        </ConfigContext.Provider>
    );
}
